
import { ItemCompact } from "@/models/ItemCompact";
import { Component, Prop, Vue } from "vue-property-decorator";

export interface RecipeCategoryPrintSection {
  categoryName: string;
  recipes: ItemCompact[];
}

function escapePrintHtml(value: string) {
  return String(value || "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/\"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

export function buildRecipeCategoryPrintDocument(
  documentTitle: string,
  sections: RecipeCategoryPrintSection[] = []
) {
  const safeDocumentTitle = escapePrintHtml(documentTitle || "Recipe Categories");
  const sectionMarkup = sections
    .map((section) => {
      const recipeItems = section.recipes
        .map((recipe) => `<li class="recipe-print">${escapePrintHtml(recipe.name)}</li>`)
        .join("");

      return `<section class="category-section">
      <div class="category-section__header">
        <h2>${escapePrintHtml(section.categoryName)}</h2>
        <p>${section.recipes.length} ${section.recipes.length === 1 ? "recipe" : "recipes"}</p>
      </div>
      ${section.recipes.length > 0
    ? `<ol class="recipe-print-list">${recipeItems}</ol>`
    : '<p class="recipe-print-empty">0 recipes</p>'}
    </section>`;
    })
    .join("");

  return `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="utf-8">
  <title>${safeDocumentTitle}</title>
  <style>
    body {
      font-family: Arial, sans-serif;
      color: #1f2933;
      margin: 0;
      background: #fff;
    }

    .recipe-category-printout {
      padding: 32px;
      background: #fff;
    }

    .category-header {
      margin-bottom: 32px;
      border-bottom: 1px solid #d9e2ec;
      padding-bottom: 12px;
    }

    .category-header h1 {
      margin: 0 0 12px;
      font-size: 28px;
      font-weight: 700;
    }

    .category-section + .category-section {
      margin-top: 32px;
      padding-top: 24px;
      border-top: 1px solid #d9e2ec;
    }

    .category-section__header {
      margin-bottom: 14px;
    }

    .category-section__header h2 {
      margin: 0 0 6px;
      font-size: 22px;
      font-weight: 700;
    }

    .category-section__header p {
      margin: 0;
      font-size: 14px;
      color: #52606d;
    }

    .recipe-print-list {
      margin: 0;
      padding-left: 24px;
    }

    .recipe-print {
      margin-bottom: 10px;
      font-size: 16px;
      line-height: 1.5;
    }

    .recipe-print-empty {
      margin: 0;
      font-size: 16px;
      color: #52606d;
      line-height: 1.5;
    }

    @media print {
      .recipe-category-printout {
        padding: 16mm;
      }
    }
  </style>
</head>
<body>
  <section class="recipe-category-printout">
    <header class="category-header">
      <h1>${safeDocumentTitle}</h1>
    </header>
    ${sectionMarkup}
  </section>
  <script>
    (function () {
      var hasClosed = false;
      function closeWindow() {
        if (hasClosed) {
          return;
        }
        hasClosed = true;
        window.close();
      }

      window.addEventListener("afterprint", function () {
        setTimeout(closeWindow, 0);
      });

      window.addEventListener("focus", function () {
        setTimeout(closeWindow, 300);
      }, { once: true });
    })();
  <\/script>
</body>
</html>`;
}

@Component
export default class vRecipeCategoryPrintout extends Vue {
  @Prop({ default: "Recipe Categories" }) readonly documentTitle!: string;
  @Prop({ default: (): RecipeCategoryPrintSection[] => [] }) readonly sections!: RecipeCategoryPrintSection[];
}
